import React, { useEffect, useCallback, useState } from 'react';
import moment from 'moment-timezone';
import { useSearchParams, useParams } from 'react-router-dom';
import { CircleCheck, Image as ImageIcon, ImageOff } from 'lucide-react';
import { deleteCompanyLogo, uploadCompanyLogo, getCompanyLogoMetaData } from '../../api/company-logo';
import { storage } from '../../constants';
import { format } from '../../utils/format-locale';
import { redirectIQ } from '../../utils/helper';
import ErrorPage from '../../components/ErrorPage';
import api from '../../api/api';
import Loading from '../../components/Loading';
import { Box, Button, Card, CardContent, CardHeader, CardTitle, Dialog, DialogActions, DialogContent, DialogTitle, FileUploader, InfoPanel, Typography } from '@otcmarketsgroup/otcm-ui-lib';

export const mimeTypes = {
  DOC: 'application/msword',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLS: 'application/vnd.ms-excel',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  PDF: 'application/pdf',
  CSV: 'text/csv',
  JPG: 'image/jpeg',
  SVG: 'image/svg+xml',
  PNG: 'image/png',
  GIF: 'image/gif',
  EPS: 'application/postscript',
  AI: 'application/postscript'
};

const allowedFileExtensions = [mimeTypes.JPG, mimeTypes.SVG, mimeTypes.PNG, mimeTypes.GIF];
const maxFileSize = 2000000;

const byteSize = format(maxFileSize, maxFileSize, 'byteSize');
const requirements = [`(.jpg, .jpeg, .png, .svg, .gif only) - ${byteSize}`,
  'All fonts must be converted to outlines. All required registered marks, service marks or trademarks should be part of the logo artwork. Corporate tag lines cannot be part of the logo artwork.'
];

const processingText = 'Your file is currently being processed. This may take a few moments. You can refresh the page to check if the processing is complete.';

const rejectText = 'The file you attempted to upload has been rejected. Please try again with a valid file.';

const CompanyLogoPage = () => {
  const [logoRejectMetaInfo, setLogoRejectMetaInfo] = useState(null);
  const [logoMetaInfo, setLogoMetaInfo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  // upload file
  const [imageSource, setImageSource] = useState(null);

  // user error
  const [error, setError] = useState(null);

  // update logo
  const [updateError, setUpdateError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [successSave, setSuccessSave] = useState(false);

  // delete
  const [deleteDialogVisible, setDialogVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [successDelete, setSuccessDelete] = useState(false);


  const [isDeletingBadFile, setIsDeletingBadFile] = useState(false);
  const [isCurrentLoaded, setCurrentLoaded] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const selectedSymbol = params.symbol;

  // render var

  const logoStatus = logoMetaInfo && logoMetaInfo.status;

  const logoProcessing = logoStatus === 'PROCESSING';

  const showLogoMetaData = (!isUpdating || logoProcessing) && logoMetaInfo;

  const logoActive = (logoStatus === 'ACTIVE');

  const showNoLogo = !logoStatus && !isUpdating && !selectedFile;
  const showProcessingLogo = logoProcessing && !isUpdating;

  const showLogoChangeControls = logoActive && !isUpdating && !successSave;
  const showFileUploader = !successSave && ((logoActive && isUpdating) || (!logoStatus));

  const loadingHeight = window?.innerHeight / 2 || 250;
  const loadDataAllow = error ? [] : logoMetaInfo ? [logoMetaInfo] : [{}];

  const handleUnload = useCallback((e) => {
    // Recommended
    e.preventDefault();
    // Included for legacy support, e.g. Chrome/Edge < 119
    e.returnValue = true;

  }, []);

  useEffect(() => {
    getCompanyLogoInfo();
  }, []);

  const loadCompanyLogoInfo = async () => {
    let rejectedMetaData, processMetaData, activeMetadata;

    try {
      const metaData = await getCompanyLogoMetaData(selectedSymbol);
      if (!metaData || metaData.length === 0) {
        setLogoRejectMetaInfo(null);
        setLogoMetaInfo(null);
        setImageSource(null);
        return false;
      }
      rejectedMetaData = metaData.find(item => item.status === 'REJECTED');
      processMetaData = metaData.find(item => item.status === 'PROCESSING');
      activeMetadata = metaData.find(item => item.status === 'ACTIVE');
      setLogoRejectMetaInfo(rejectedMetaData);
      if (rejectedMetaData) {
        if (activeMetadata) {
          setLogoMetaInfo(activeMetadata);
        } else {
          setLogoMetaInfo(activeMetadata);
          setImageSource(null);
          return false;
        }
      } else if (processMetaData) {
        setLogoMetaInfo(processMetaData);
        return false;
      } else if (activeMetadata) {
        setLogoMetaInfo(activeMetadata);
      }

      return true;
    } catch (err) {
      const errDetails = err.response.data;
      console.error(errDetails);
      if (errDetails && errDetails.code === 403) {
        setError('We\'re sorry, there is a problem with the current request. Details regarding this message have been logged. Please try again and Contact us if the problem continues.');
      } else {
        setError('We\'re sorry, there is a problem with the current request. Details regarding this message have been logged. Please try again and Contact us if the problem continues.');
      }
      return false;
    }
  };

  const getCompanyLogoInfo = async () => {
    let userToken = null;
    const token = searchParams.get('token');
    if (token) {
      window.sessionStorage.setItem(storage.TOKEN, token);
      userToken = token;
      setSearchParams({});
    } else {
      userToken = window.sessionStorage.getItem(storage.TOKEN);
    }

    if (userToken) {
      const allowLogoLoad = await loadCompanyLogoInfo();
      if (allowLogoLoad) {
        try {
          //const imageBlob = await getCompanyLogo(selectedSymbol);
          //const imageObjectURL = URL.createObjectURL(imageBlob);
          const url = api.link({ url: `/company/logo?symbol=${selectedSymbol}` });
          setImageSource(url);
          setCurrentLoaded(true);
        } catch (err) {
          console.error(err);
          setError('Logo currently not available. Try again.');
        }
      }
      else {
        setCurrentLoaded(true);
      }
    } else {
      redirectIQ(); // no token
    }
  };

  const handleLogoUpdate = () => {
    setIsUpdating(true);
  };

  const cancelLogoUpdate = () => {
    if (logoActive) {
      const url = api.link({ url: `/company/logo?symbol=${selectedSymbol}` });
      setImageSource(url);
    } else {
      setImageSource(null);
    }
    setSelectedFile(null);
    setUpdateError(null);
    setIsUpdating(false);
    window.removeEventListener('beforeunload', handleUnload, true);
  };

  const handleConfirmUpdateLogo = _ => {
    setUpdateError(null);
    if (!isUpdating) setIsUpdating(true);
    if (!selectedFile) {
      setUpdateError('Please select file.');
      return;
    }
    setIsSaving(true);

    uploadCompanyLogo(selectedSymbol, selectedFile)
      .then(_ => {
        setIsSaving(false);
        setSuccessSave(true);
        setSelectedFile(null);
        window.removeEventListener('beforeunload', handleUnload, true);
        loadCompanyLogoInfo();
      })
      .catch(e => {
        console.log(e);
        setIsSaving(false);
        setUpdateError('Company Logo submit failed. Try again.');
      });
  };

  const handleDeleteShow = () => {
    setDialogVisible(true);
  };

  const handleConfirmDeleteLogo = _ => {
    setDeleteError(null);
    setIsDeleting(true);

    deleteCompanyLogo(selectedSymbol, logoMetaInfo.uid)
      .then(_ => {
        setIsDeleting(false);
        setSuccessDelete(true);
        window.removeEventListener('beforeunload', handleUnload);
        loadCompanyLogoInfo();
      })
      .catch(e => {
        console.log(e);
        setIsDeleting(false);
        setDeleteError('Company Logo delete failed. Try again.');
      });
  };

  const ackRejectedDeleteLogo = _ => {
    setIsDeletingBadFile(true);
    deleteCompanyLogo(selectedSymbol, logoRejectMetaInfo.uid)
      .then(_ => {
        setIsDeletingBadFile(true);
        loadCompanyLogoInfo();
      })
      .catch(e => {
        setIsDeletingBadFile(true);
        console.log(e);
      });
  };

  const handleDeleteHide = () => {
    setDialogVisible(false);
    if (successDelete) {
      getCompanyLogoInfo();
      setSuccessDelete(false);
    }
  };

  const handleUploadedFile = file => {
    setUpdateError(null);
    if (file) {
      const fileReader = new FileReader();
      setSelectedFile(file);
      window.addEventListener('beforeunload', handleUnload, true);
      fileReader.onload = () => {
        setImageSource(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    } else {
      setSelectedFile(null);
      setImageSource(null);
      window.removeEventListener('beforeunload', handleUnload, true);
    }
  };

  return (
    <Box key='companyLogoPageKey'>
      {error ? <ErrorPage /> : <Loading height={loadingHeight} data={loadDataAllow} loaded={isCurrentLoaded} error={error}>
        <Card>
          <CardHeader>
            <CardTitle icon={<ImageIcon />}>Company Logo</CardTitle>
          </CardHeader>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant='body1' gutterBottom>
                To help investors recognize companies, OTC Markets Group Inc. uses company logos instead of or in combination with trading symbols. Company logos are used on the websites maintained by OTC Markets Group Inc, <Box component='span' sx={{ color: 'var(--otc-green)', ':hover': { textDecoration: 'underline' } }}><a href='https://www.otcmarkets.com' target='_blank' rel='noopener noreferrer'>www.otcmarkets.com</a></Box>, and <Box component='span' sx={{ color: 'var(--otc-green)', ':hover': { textDecoration: 'underline' } }}><a href='https://www.otciq.com' target='_blank' rel='noopener noreferrer'>www.otciq.com</a></Box>.
              </Typography>
              <Typography variant='body1'>
                OTC Markets Group Inc.'s use of your company's logos, trademarks and service marks, and your company's respective rights, privileges, and obligations with respect to such use, are governed by the <Box component='span' sx={{ color: 'var(--otc-green)', ':hover': { textDecoration: 'underline' } }}><a href='https://www.otcmarkets.com/files/Issuer_Services_Agreement.pdf' target='_blank' rel='noopener noreferrer' >Issuer Services Agreement</a></Box>.
              </Typography>
              {showNoLogo && <Box id='noLogo' sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'var(--otc-light-gray)', padding: 3, width: '12em' }}>
                <ImageOff />
                <Typography variant='h3'>No Logo</Typography>
              </Box>}
              {showProcessingLogo && <Box id='noLogo'>PROCESSING</Box>}

              {imageSource && <Box id='logoPreview'>
                {imageSource && <Box component='img' sx={{ maxWidth: '12em' }} src={imageSource} alt={selectedSymbol} />}
              </Box>}

              {showLogoMetaData && <Box id='logoMeta'>
                <Typography variant='body1'>
                  <span style={{ fontWeight: 700 }}>Last Modified By </span>
                  {logoMetaInfo.lastModifierName}
                </Typography>
                <Typography variant='body1'>
                  <span style={{ fontWeight: 700 }}>Last Modified On </span>
                  {moment(logoMetaInfo.lastModifiedDate).format('llll')}
                </Typography>
              </Box>}

              {logoProcessing && <InfoPanel title={processingText} />}

              {showLogoChangeControls && <Box display='flex' gap={2}>
                <Button id='deleteLogo' color='warning' disabled={isDeleting} onClick={handleDeleteShow}>Delete Logo</Button>
                <Button id='changeLogo' disabled={isDeleting} onClick={handleLogoUpdate}>Change Logo</Button>
              </Box>}

              {showFileUploader && <>
                <FileUploader
                  files={selectedFile ? [selectedFile] : []}
                  onFileSelect={files => handleUploadedFile(files && files.length > 0 ? files[0] : null)}
                  acceptedFileTypes={allowedFileExtensions}
                  maxFileSize={maxFileSize}
                  fileInstructions={requirements.join(' ')}
                />
                {(selectedFile || isUpdating) && <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button variant='text' onClick={cancelLogoUpdate} disabled={isSaving}>Cancel</Button>
                  <Button onClick={handleConfirmUpdateLogo} disabled={isSaving} isLoading={isSaving}>Submit</Button>
                </Box>}
              </>
              }
              {updateError && <InfoPanel isWarning title={updateError} />}
              {error && <InfoPanel isWarning title={error} />}
              {logoRejectMetaInfo && <InfoPanel isWarning title={rejectText}>
                <Button disabled={isDeletingBadFile} onClick={ackRejectedDeleteLogo} isLoading={isDeletingBadFile}>Ok</Button>
              </InfoPanel>}
            </Box>
          </CardContent>
        </Card>
      </Loading>}
      <Dialog
        open={deleteDialogVisible}
        onClose={handleDeleteHide}
      >
        <DialogTitle title='Delete Logo' onClose={handleDeleteHide} />
        {successDelete ? <>
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircleCheck color='var(--otc-green)' />
              <Typography variant='body1' color='var(--otc-green)'>{selectedSymbol} Logo has successfully been deleted.</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteHide}>OK</Button>
          </DialogActions>
        </> : <>
          <DialogContent>
            <Typography variant='body1'>You have elected to delete your logo. It will no longer appear on any OTC Markets Group Inc. products. Do you still want to delete?</Typography>
            {deleteError && <InfoPanel isWarning title={deleteError} />}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color='warning' disabled={isDeleting} onClick={handleDeleteHide}>Cancel</Button>
            <Button color='warning' disabled={isDeleting} isLoading={isDeleting} onClick={handleConfirmDeleteLogo}>Delete</Button>
          </DialogActions>
        </>}
      </Dialog>
    </Box>
  );
};

export default CompanyLogoPage;
